import * as React from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

/** Props for the {@link ConfirmModal} component. */
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Modal heading. Defaults to "Clear Chat History?". */
  title?: string;
  /** Body content rendered below the heading. Accepts JSX. */
  message?: React.ReactNode;
  /** Label for the confirm action button. Defaults to "Clear Chat". */
  confirmLabel?: string;
}

/** Reusable confirmation modal rendered via portal. */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Clear Chat History?',
  message = (
    <>
      Are you sure you want to delete all messages in this conversation?{' '}
      <span className='font-bold'>This action cannot be undone.</span>
    </>
  ),
  confirmLabel = 'Clear Chat',
}) =>
  createPortal(
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className='border-0'
      style={{
        background:
          'linear-gradient(180deg, rgba(255, 255, 255, 0) -1.92%, #A28DF6 140.84%), #FFFFFF',
      }}
    >
      <div className='flex flex-col items-center gap-6 pt-2 text-center'>
        <div className='flex flex-col gap-2'>
          <h2 className='font-proxima text-24 font-bold leading-25 tracking-normal text-brand-800'>
            {title}
          </h2>
          <p className='mt-4 font-sans text-16 font-normal leading-21 text-gray-600'>{message}</p>
        </div>

        <div className='flex w-full items-center justify-between gap-2'>
          <button
            onClick={onConfirm}
            className={clsx(
              'font-sans text-16 font-semibold leading-27 tracking-normal',
              'text-black',
              'bg-transparent transition-opacity hover:opacity-70',
              'whitespace-nowrap'
            )}
          >
            {confirmLabel}
          </button>

          <Button onClick={onClose} variant='gray-variant' className='!rounded-30'>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>,
    document.body
  );

/** @deprecated Use ConfirmModal directly. This alias preserves backwards compatibility. */
export const ClearChatModal = ConfirmModal;
